import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { MessageCircle } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";

/** Build the WhatsApp-ready message for a project. */
function buildMessage(p) {
  if (!p) return "";
  const lines = [];
  lines.push(`*${p.project_name}*`);
  if (p.developer_name) lines.push(`_by ${p.developer_name}_`);
  if (p.location) lines.push(`📍 ${p.location}`);
  lines.push("");

  const stats = [];
  if (p.total_towers) stats.push(`🏢 ${p.total_towers} towers`);
  if (p.total_floors) stats.push(`🏗️ ${p.total_floors} floors`);
  if (p.total_land_parcel) stats.push(`🌳 ${p.total_land_parcel} land`);
  if (stats.length) {
    lines.push(stats.join(" · "));
    lines.push("");
  }

  if (p.rera_possession) {
    lines.push(`*RERA / Possession:* ${p.rera_possession}`);
  }

  if ((p.configurations || []).length) {
    lines.push("");
    lines.push("*Configurations:*");
    for (const c of p.configurations) {
      if (!c.variants || c.variants.length === 0) {
        lines.push(`• ${c.bhk}`);
        continue;
      }
      for (const v of c.variants) {
        const sqft = v.sqft ? `${v.sqft} sqft` : "";
        const price = v.price ? ` — ${v.price}` : "";
        lines.push(`• ${c.bhk}${sqft ? ` · ${sqft}` : ""}${price}`);
      }
    }
  }

  if (p.developer_commitment) {
    lines.push("");
    lines.push(`*Developer commitment:* ${p.developer_commitment}`);
  }

  if ((p.amenities || []).length) {
    lines.push("");
    lines.push(`*Amenities included:* ${p.amenities.length}+ world-class amenities — clubhouse, pool, gym, jogging track, kids play, EV charging & more.`);
  }

  if (p.comments) {
    lines.push("");
    lines.push(p.comments);
  }

  lines.push("");
  lines.push("Would you like a site visit or more details? I'm here to help. 🙂");
  return lines.join("\n");
}

/** Keep digits only. wa.me refuses non-digits and leading zeroes. */
function sanitizePhone(v) {
  return String(v || "").replace(/[^\d]/g, "");
}

export default function ShareProjectDialog({ project, open, onOpenChange }) {
  const [phone, setPhone] = useState("");
  const [countryCode, setCountryCode] = useState("91");
  const [msg, setMsg] = useState("");

  useEffect(() => {
    if (open) {
      setPhone("");
      setMsg(buildMessage(project));
    }
  }, [open, project]);

  const cleanedPhone = useMemo(() => sanitizePhone(phone), [phone]);
  const cleanedCc = useMemo(() => sanitizePhone(countryCode) || "91", [countryCode]);
  const canSend = cleanedPhone.length >= 6;

  const openWhatsApp = () => {
    if (!canSend) {
      toast.error("Please enter a valid phone number");
      return;
    }
    // Strip a leading country-code duplicate if the user pasted the full number.
    let num = cleanedPhone;
    if (num.startsWith(cleanedCc) && num.length > 10) {
      // fine — already includes CC
    } else {
      num = `${cleanedCc}${num}`;
    }
    const url = `https://wa.me/${num}?text=${encodeURIComponent(msg)}`;
    window.open(url, "_blank", "noopener,noreferrer");
    toast.success("Opening WhatsApp…");
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="share-project-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <MessageCircle className="h-5 w-5 text-emerald-600" />
            Share on WhatsApp
          </DialogTitle>
          <DialogDescription>
            Enter your client&apos;s phone number to open a WhatsApp chat pre-filled
            with this project&apos;s details.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="grid grid-cols-[90px,1fr] gap-2">
            <div>
              <Label>Code</Label>
              <Input
                data-testid="share-country-code"
                value={countryCode}
                onChange={(e) => setCountryCode(e.target.value)}
                placeholder="91"
                inputMode="numeric"
                maxLength={4}
              />
            </div>
            <div>
              <Label>Client&apos;s WhatsApp number</Label>
              <Input
                data-testid="share-phone-input"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="10-digit mobile number"
                inputMode="tel"
              />
            </div>
          </div>

          <div>
            <Label className="flex items-center justify-between">
              <span>Message preview</span>
              <span className="text-[11px] font-normal text-slate-400">edit before sending</span>
            </Label>
            <textarea
              data-testid="share-message"
              value={msg}
              onChange={(e) => setMsg(e.target.value)}
              rows={10}
              className="w-full rounded-lg border border-slate-200 bg-slate-50/60 px-3 py-2 text-[12px] leading-relaxed outline-none focus:border-slate-400 font-mono"
            />
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            data-testid="share-cancel"
          >
            Cancel
          </Button>
          <Button
            data-testid="share-send-btn"
            className="bg-emerald-600 text-white hover:bg-emerald-700"
            disabled={!canSend}
            onClick={openWhatsApp}
          >
            <MessageCircle className="h-4 w-4" /> Send via WhatsApp
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
